/**
 * Turns a phrase into the name of its audio file.
 *
 * The build-time half of this pair is `tools/audio/speech-keys.mjs` (and the
 * same function again in `tools/audio/render.py`). All three must agree
 * character for character, or the app asks for clips that were never rendered.
 * `npm run audio` re-checks the Node/Python pair on every run.
 */

/**
 * Collapse whitespace and settle on one Unicode spelling.
 *
 * Arabic can be written with the same letters composed in different ways
 * (NFC vs NFD). Without normalising, two strings that look identical would hash
 * differently and one of them would silently have no audio.
 */
export function normalizeSpeech(text: string): string {
  return text.normalize('NFC').replace(/\s+/g, ' ').trim();
}

const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK64 = 0xffffffffffffffffn;

/**
 * FNV-1a over the UTF-8 bytes, as 16 hex characters.
 *
 * Deliberately not SHA-1 via WebCrypto: `crypto.subtle` exists only in a secure
 * context, so it is missing whenever the app is served over plain http on a
 * home network — which is exactly what `npm run share` does. This works
 * everywhere and needs no await.
 */
export function clipKey(text: string): string {
  const bytes = new TextEncoder().encode(normalizeSpeech(text));
  let hash = FNV_OFFSET;
  for (const byte of bytes) {
    hash = ((hash ^ BigInt(byte)) * FNV_PRIME) & MASK64;
  }
  return hash.toString(16).padStart(16, '0');
}
